/**
 * ParentConsoleNav.jsx — Cycle 60 · Phase D · seven-item Parent /
 * Teacher console navigation. Renders as a fixed left sidebar on
 * desktop and a horizontal, scroll-snapping pill strip on mobile so
 * every parent surface presents the same taxonomy without touching
 * the underlying page code.
 *
 * Mounted from Layout.jsx whenever the route matches a parent surface
 * (`/parents/*`, `/safety`, `/achievements`, `/family`, `/my-learning`).
 *
 * Routes chosen to reuse EXISTING pages — no rewrites:
 *   Overview       → /parents
 *   Learning Paths → /my-learning
 *   Weekly Plan    → /parents/plan
 *   Schedule       → /parents/schedule
 *   Progress       → /parents/dashboard
 *   Certificates   → /achievements
 *   Safety         → /safety
 *   Parent Console → /parents/dashboard
 */
import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Map as MapIcon,
  CalendarDays,
  CalendarClock,
  BarChart3,
  Award,
  ShieldCheck,
  Users,
} from "lucide-react";

export const PARENT_CONSOLE_ITEMS = [
  { key: "overview",      to: "/parents",           label: "Overview",       icon: LayoutDashboard, exact: true },
  { key: "learning-paths",to: "/my-learning",       label: "Learning Paths", icon: MapIcon,         exact: false },
  { key: "weekly-plan",   to: "/parents/plan",      label: "Weekly Plan",    icon: CalendarDays,    exact: false },
  { key: "schedule",      to: "/parents/schedule",  label: "Schedule",       icon: CalendarClock,   exact: false },
  { key: "progress",      to: "/parents/dashboard", label: "Progress",       icon: BarChart3,       exact: true },
  { key: "certificates",  to: "/achievements",      label: "Certificates",   icon: Award,           exact: false },
  { key: "safety",        to: "/safety",            label: "Safety",         icon: ShieldCheck,     exact: false },
  { key: "console",       to: "/parents/dashboard", label: "Parent Console", icon: Users,           exact: false },
];

const linkClass = ({ isActive }) =>
  `w-full inline-flex items-center gap-2 h-10 md:h-11 px-3 rounded-xl border-2 shrink-0 whitespace-nowrap font-bold text-[13px] transition-all ${
    isActive
      ? "bg-slate-900 text-white border-slate-900 shadow-[0_3px_0_0_rgba(15,23,42,0.35)]"
      : "bg-white border-slate-900 text-slate-800 hover:-translate-y-0.5 hover:shadow-[0_3px_0_0_rgba(15,23,42,1)]"
  }`;

export default function ParentConsoleNav() {
  return (
    <nav
      aria-label="Parent and Teacher navigation"
      data-testid="parent-console-nav"
      className="mb-4 md:mb-6 min-w-0 max-w-full overflow-hidden"
    >
      <p className="mb-1.5 text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-500">
        Parents &amp; Teachers
      </p>
      <ul className="flex md:flex-wrap gap-1.5 overflow-x-auto scrollbar-hide px-1 pb-1 md:pb-0 max-w-full">
        {PARENT_CONSOLE_ITEMS.map(({ key, to, label, icon: Icon, exact }) => (
          <li key={key} className="shrink-0">
            <NavLink
              to={to}
              end={exact}
              data-testid={`parent-console-nav-${key}`}
              className={linkClass}
            >
              <Icon size={15} strokeWidth={3} />
              <span>{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
